<script setup>
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Ticket',
  },
})

const { useAbility } = await import('@casl/vue')
const ability = useAbility()

const tickets = ref([])
const loading = ref(false)

const PRIORITY_META = {
  low: { label: 'Rendah', color: 'secondary' },
  medium: { label: 'Sedang', color: 'info' },
  high: { label: 'Tinggi', color: 'warning' },
  urgent: { label: 'Urgent', color: 'error' },
}

const STATUS_META = {
  open: { label: 'Terbuka', color: 'info' },
  in_progress: { label: 'Diproses', color: 'warning' },
  resolved: { label: 'Selesai', color: 'success' },
  closed: { label: 'Ditutup', color: 'secondary' },
}

const CATEGORY_LABELS = {
  billing: 'Tagihan',
  technical: 'Teknis',
  feature_request: 'Request Fitur',
  other: 'Lainnya',
}

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const fetchTickets = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/v1/tickets').get()
    const body = response?.data ?? response

    tickets.value = body?.data ?? []
  } catch (err) {
    console.error('[Support] fetch tickets error', err)
    tickets.value = []
  } finally {
    loading.value = false
  }
}

onMounted(fetchTickets)

const headers = [
  { title: 'Subjek', key: 'subject' },
  { title: 'Kategori', key: 'category' },
  { title: 'Prioritas', key: 'priority' },
  { title: 'Status', key: 'status' },
  { title: 'Dibuat', key: 'created_at' },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 100 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Bantuan / Support
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Ajukan keluhan/pertanyaan ke tim PintuKasir, dan pantau balasannya di sini
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'Ticket')"
            prepend-icon="ri-add-line"
            :to="{ name: 'support-create' }"
          >
            Buat Tiket Baru
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="tickets"
        :loading="loading"
        items-per-page="-1"
        hide-default-footer
      >
        <template #item.subject="{ item }">
          <RouterLink
            :to="{ name: 'support-id', params: { id: item.id } }"
            class="text-decoration-none font-weight-medium"
          >
            {{ item.subject }}
          </RouterLink>
        </template>

        <template #item.category="{ item }">
          <span class="text-body-2">{{ item.category ? CATEGORY_LABELS[item.category] : '-' }}</span>
        </template>

        <template #item.priority="{ item }">
          <VChip
            :color="PRIORITY_META[item.priority]?.color"
            variant="tonal"
            size="small"
          >
            {{ PRIORITY_META[item.priority]?.label ?? item.priority }}
          </VChip>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="STATUS_META[item.status]?.color"
            variant="tonal"
            size="small"
          >
            {{ STATUS_META[item.status]?.label ?? item.status }}
          </VChip>
        </template>

        <template #item.created_at="{ item }">
          <span class="text-body-2">{{ formatDate(item.created_at) }}</span>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            icon="ri-eye-line"
            variant="text"
            size="small"
            color="primary"
            :to="{ name: 'support-id', params: { id: item.id } }"
          />
        </template>

        <template #no-data>
          <div class="text-center py-10">
            <VIcon
              icon="ri-customer-service-2-line"
              size="64"
              color="secondary"
              class="mb-4"
            />
            <p class="text-h6 text-medium-emphasis">
              Belum ada tiket
            </p>
            <p class="text-body-2 text-medium-emphasis">
              Klik "Buat Tiket Baru" kalau ada keluhan/pertanyaan untuk tim PintuKasir
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>
  </div>
</template>
